
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  Compass,
  Heart,
  User,
  Plus
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useNotifications } from "@/hooks/useNotifications";
import { toast } from "sonner";

const BottomNavBar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { data: notifications } = useNotifications();
  const hasUnreadNotifications = !!user && (notifications?.some((n) => !n.read) ?? false);
  const profilePath = user ? "/profile" : "/signin?from=/profile";
  const pathname = location.pathname;
  
  // Determine active tab for nested routes
  const isHomeActive = pathname === '/';
  const isDiscoverActive = pathname === '/discover' || pathname.startsWith('/discover/') || pathname === '/search' || pathname.startsWith('/artists') || pathname.startsWith('/tours') || pathname.startsWith('/concerts') || pathname === '/featured' || pathname.startsWith('/venues');
  const isActivityActive = pathname === '/activity';
  const isProfileActive = pathname === '/profile' || pathname.startsWith('/profile/');
  
  const scrollToTop = (path: string) => {
    if (location.pathname === path) {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  };
  
  // Show bottom nav for all users, but some actions require login
  
  return (
    <div className="fixed bottom-0 inset-x-0 bg-background/85 dark:bg-[hsl(260_25%_6%/0.78)] backdrop-blur-xl border-t border-border/60 dark:border-white/[0.06] dark:shadow-[0_-8px_32px_-12px_rgba(0,0,0,0.6)] py-3 z-40" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0px)' }}>
      <div className="grid grid-cols-5 items-center justify-between w-full">
        <Link 
          to="/" 
          onClick={() => scrollToTop("/")}
          className={cn(
            "flex flex-col items-center justify-center",
            isHomeActive ? "text-primary" : "text-muted-foreground"
          )}
          aria-label="Home"
        >
          <Home className="h-6 w-6" strokeWidth={1.5} />
        </Link>
        
        <Link 
          to="/discover" 
          onClick={() => scrollToTop("/discover")}
          className={cn(
            "flex flex-col items-center justify-center",
            isDiscoverActive ? "text-primary" : "text-muted-foreground"
          )}
          aria-label="Discover"
        >
          <Compass className="h-6 w-6" strokeWidth={1.5} />
        </Link>
        
        <Link to="/reviews/new" className="flex flex-col items-center justify-center" aria-label="New Review">
          <div className="bg-[#8b5cf6] text-white p-2 rounded-md">
            <Plus className="h-6 w-6" strokeWidth={2} />
          </div>
        </Link>
        
        <Link 
          to={user ? "/activity" : "/signin?from=/activity"}
          onClick={() => {
            if (!user) {
              toast.info('Please sign in to view activity');
            } else {
              scrollToTop('/activity');
            }
          }}
          className={cn(
            "flex flex-col items-center justify-center",
            isActivityActive ? "text-primary" : "text-muted-foreground"
          )}
          aria-label="Activity"
        >
          <span className="relative">
            <Heart className="h-6 w-6" strokeWidth={1.5} />
            {hasUnreadNotifications && (
              <span
                className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-destructive"
                aria-hidden="true"
              />
            )}
          </span>
        </Link>
        
        <Link 
          to={profilePath}
          onClick={() => {
            if (!user) {
              toast.info('Please sign in to view your profile');
            } else {
              scrollToTop('/profile');
            }
          }}
          className={cn(
            'flex flex-col items-center justify-center',
            isProfileActive ? 'text-primary' : 'text-muted-foreground'
          )}
          aria-label="Profile"
        >
          <User className="h-6 w-6" strokeWidth={1.5} />
        </Link>
      </div>
    </div>
  );
};

export default BottomNavBar;
